import { Action } from "redux";
import { fork, take } from "redux-saga/effects";

import { SagaWithReturnType } from "../types/react";
import { IResultAction, IResultActionError, IResultActionSuccess } from "./types";

type anyArray = any[];

/**
 * Pomocná funkce pro tvorbu výsledkových akcí. Zajišťuje typovou kontrolu nad možnými kombinacemi chybového a úspěšného
 * obsahu akce.
 *
 * @param type Typ akce, která se má vytvořit.
 * @param successPayload Obsah pro akci v případě, že akce byla úspěšná.
 * @param errorPayload Obsah pro akci v případě, že akce skončila chybou.
 * @throws Vyhazuje chybu v případě, že není zadán žádný obsah, nebo když je najednou zadán úspěšný i chybový obsah.
 * @see Detaily použití v conventions/redux/actions.md.
 */
export const createResultAction = <T, R, E>(type: T, successPayload?: R, errorPayload?: E): IResultAction<T, R, E> => {
    if (successPayload !== undefined && errorPayload === undefined) {
        return {
            type,
            payload: successPayload,
            error: false,
        };
    } else if (errorPayload !== undefined && successPayload === undefined) {
        return {
            type,
            payload: errorPayload,
            error: true,
        };
    } else {
        throw new Error("[Redux] Invalid parameters for createResultAction function. You must provide result or error.");
    }
};

/**
 * Zkontroluje, zda je zadaná akce výsledkovou akcí daného typu, která skončila úspěchem.
 * Funguje jako TypeGuard.
 * @param action Akce ke kontrole.
 * @param type Typ výsledkové akce, kterou hledáme (hodnota typu).
 * @template A Typ výsledkové akce, kterou hledáme (interface).
 * @see Detaily použití v conventions/redux/actions.md.
 */
export const isSuccessAction = <A>(action: Action, type: string): action is IResultActionSuccess<A> => {
    return action.type === type && action instanceof IResultActionSuccess;
};

/**
 * Zkontroluje, zda je zadaná akce výsledkovou akcí daného typu, která skončila chybou.
 * Funguje jako TypeGuard.
 * @param action Akce ke kontrole.
 * @param type Typ výsledkové akce, kterou hledáme (hodnota typu).
 * @template A Typ výsledkové akce, kterou hledáme (interface).
 * @see Detaily použití v conventions/redux/actions.md.
 */
export const isErrorAction = <A>(action: Action, type: string): action is IResultActionError<A> => {
    return action.type === type && action instanceof IResultActionError;
};
/**
 * Pomocná sága pro odchytávání výsledkových akcí.
 * @param pattern Typ akce, na kterou se čeká.
 * @param successSaga Sága, která se spustí, pokud byla akce úspěšná. Jako parametr dostane odchycenou akci.
 * @param errorSaga Sága, která se spustí, pokud akce skončila chybou. Jako parametr dostane odchycenou akci.
 * @param args Dodatečné parametry pro spouštěné ságy. Parametry jsou společné pro úspěšnou i chybovou ságu.
 * @throws Vyhazuje chybu, pokud odchycená akce není výsledkovou akcí.
 * @see Detaily použití v conventions/redux/actions.md.
 */
export const takeResultAction: SagaWithReturnType<[string, SagaWithReturnType<[...anyArray]>, SagaWithReturnType<[...anyArray]>, ...anyArray]> = function* takeResultAction(pattern, successSaga, errorSaga, ...args) {
    const action: Action = yield take(pattern);
    if (action instanceof IResultActionSuccess) {
        yield fork(successSaga, action, ...args);
    } else if (action instanceof IResultActionError) {
        yield fork(errorSaga, action, ...args);
    } else {
        throw new Error("[Saga helper takeResultAction] Action specified by pattern <" + pattern + "> is not a result action.");
    }
};
